import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Star, ShoppingCart, ChevronLeft, ChevronRight, Info, Check, Truck, Shield, Plus } from 'lucide-react';
import './ProductDetailPage.css';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { api } from '../../utils/axiosConfig';
// import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [sliderIndex, setSliderIndex] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const { accessToken, isAuthenticated } = useAuth(); // Get auth state
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState("0% 0%");
  const mainImageRef = useRef(null);
  const DESCRIPTION_MAX_LENGTH = 200;
  const { addToCart: addToCartContext, fetchCartData } = useCart();

  // Fixed getImageUrl function
  const handleZoomMove = (e) => {
    const rect = mainImageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos(`${x}% ${y}%`);
  };
  const getImageUrl = (imagePath) => {
    console.log("Processing image path:", imagePath); // Debug log

    if (!imagePath) {
      console.log("No image path provided");
      return "https://via.placeholder.com/400x400?text=No+Image";
    }

    // If it's already a full URL (http/https), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      console.log("Full URL detected:", imagePath);
      return imagePath;
    }

    // Get base URL from environment or current location
    const baseUrl = process.env.REACT_APP_BASE_URL?.split('/api')[0] || window.location.origin;

    // Handle different path formats
    let finalUrl;
    if (imagePath.startsWith('/')) {
      // Path starts with /, use as absolute path
      finalUrl = `${baseUrl}${imagePath}`;
    } else {
      // Path doesn't start with /, add leading slash
      finalUrl = `${baseUrl}/${imagePath}`;
    }

    console.log("Constructed URL:", finalUrl);
    return finalUrl;
  };

  // Get logged in user (you might get this from context/state management)
  const getCurrentUser = () => {
    // Replace this with your actual user authentication logic
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  };

  // Add to Cart function for logged-in users

  const addToCart = async (productId, quantity) => {
    try {
      // Check if user is logged in
      console.log('Adding to cart:', { productId, quantity });
      if (!isAuthenticated || !accessToken) {
        // Show SweetAlert login prompt
        await Swal.fire({
          title: 'Not Logged In',
          text: 'Please login to add items to cart.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Login',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            // Redirect to login page (or trigger login modal)
            window.location.href = '/login';
          }
        });

        return null;
      }

      console.log('Adding to cart:', { productId, quantity });

      // Use the configured axios instance (handles token refresh automatically)
      const response = await api.post('/cart/add', {
        productId,
        quantity
      });

      console.log('Product added to cart:', response.data);

      // Show success message
      await Swal.fire({
        title: 'Added to Cart!',
        text: `You added ${quantity} item(s) to your cart.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      return response.data.cart;

    } catch (error) {
      console.error('Error adding to cart:', error);

      // Handle specific error codes
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        console.log('Token expired, will retry automatically');
      } else if (error.response?.data?.code === 'NO_TOKEN') {
        await Swal.fire({
          title: 'Not Logged In',
          text: 'Please login to add items to cart.',
          icon: 'warning',
          confirmButtonText: 'Login'
        }).then(() => {
          window.location.href = '/login';
        });
      } else {
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add product to cart',
          icon: 'error'
        });
      }

      throw error;
    }
  };


  // Alternative method for guest users (store in localStorage)
  // const addToGuestCart = (product, quantity) => {
  //   if (!product) return;

  //   try {
  //     const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');

  //     // Check if product already exists in cart
  //     const existingItemIndex = guestCart.findIndex(
  //       item => item.productId === (product.id || product._id)
  //     );

  //     if (existingItemIndex > -1) {
  //       // Update quantity if item exists
  //       guestCart[existingItemIndex].quantity += quantity;
  //     } else {
  //       // Add new item to cart
  //       const cartItem = {
  //         _id: Date.now().toString(), // Temporary ID for guest cart
  //         productId: product.id || product._id,
  //         productName: product.name,
  //         productImage: product.images && product.images.length > 0 ? product.images[0].path : null,
  //         price: product.price,
  //         quantity: quantity,
  //         productCategory: product.category,
  //         rating: product.rating || 4.0,
  //         inStock: true
  //       };
  //       guestCart.push(cartItem);
  //     }

  //     localStorage.setItem('guestCart', JSON.stringify(guestCart));
  //     console.log('Product added to guest cart');
  //   } catch (error) {
  //     console.error('Error adding to guest cart:', error);
  //     throw error;
  //   }
  // };

  // Main add to cart handler
  const handleAddToCart = async () => {
    if (!product) return;

    // Check authentication first
    if (!isAuthenticated) {
      alert('Please login to add items to your cart');
      // Optionally: open login modal or navigate to login page
      // navigate('/login');
      return;
    }

    setAddingToCart(true);
    setAddToCartSuccess(false);

    try {
      // Only for logged-in users now
      // await addToCartContext(product,quantity)
      await addToCart(product.id || product._id, quantity);
      await fetchCartData();
      setAddToCartSuccess(true);
      setTimeout(() => setAddToCartSuccess(false), 2000);

    } catch (error) {
      console.error('Add to cart error:', error);
      // Error already handled in addToCart function
    } finally {
      setAddingToCart(false);
    }
  };

  // Fetch recommended products based on category
  const fetchRecommendedProducts = useCallback(async (category, currentProductId) => {
    if (!category) return;

    try {
      setRecommendationsLoading(true);

      // Try fetching from both endpoints
      let allProducts = [];

      try {
        const response1 = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/products/interrando`
        );
        const products1 = Array.isArray(response1.data) ? response1.data :
          (response1.data?.data && Array.isArray(response1.data.data)) ? response1.data.data : [];
        allProducts = [...allProducts, ...products1];
      } catch (err) {
        console.log('Failed to fetch from interrando endpoint for recommendations');
      }

      try {
        const response2 = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/products/Allproducts`
        );
        const products2 = Array.isArray(response2.data) ? response2.data :
          (response2.data?.data && Array.isArray(response2.data.data)) ? response2.data.data : [];
        allProducts = [...allProducts, ...products2];
      } catch (err) {
        console.log('Failed to fetch from Allproducts endpoint for recommendations');
      }

      // Remove duplicates based on ID
      const uniqueProducts = allProducts.filter((product, index, self) =>
        index === self.findIndex(p => (p.id || p._id) === (product.id || product._id))
      );

      // Filter products by category and exclude current product
      const categoryProducts = uniqueProducts.filter(p => {
        const productCategory = p.category?.toLowerCase() || '';
        const targetCategory = category.toLowerCase() || '';
        const productId = p.id || p._id;

        return productCategory === targetCategory &&
          productId !== currentProductId &&
          productId !== parseInt(currentProductId);
      });

      // Shuffle and limit to 8 products
      const shuffled = categoryProducts.sort(() => 0.5 - Math.random());
      setRecommendedProducts(shuffled.slice(0, 8));

    } catch (error) {
      console.error('Error fetching recommended products:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        let response;
        let foundProduct = null;

        try {
          response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/products/interrando`
          );

          const productsArray = Array.isArray(response.data) ? response.data :
            (response.data?.data && Array.isArray(response.data.data)) ? response.data.data :
              [];

          foundProduct = productsArray.find(p =>
            p.id === productId ||
            p._id === productId ||
            p.id === parseInt(productId) ||
            p._id === parseInt(productId)
          );

        } catch (err) {
          console.log('Failed to fetch from interrando endpoint, trying Allproducts:', err.message);
        }

        if (!foundProduct) {
          try {
            response = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/products/Allproducts`
            );

            const productsArray = Array.isArray(response.data) ? response.data :
              (response.data?.data && Array.isArray(response.data.data)) ? response.data.data :
                [];

            foundProduct = productsArray.find(p =>
              p.id === productId ||
              p._id === productId ||
              p.id === parseInt(productId) ||
              p._id === parseInt(productId)
            );

          } catch (err) {
            console.log('Failed to fetch from Allproducts endpoint:', err.message);
          }
        }

        if (!foundProduct) {
          throw new Error('Product not found in any endpoint');
        }

        console.log('Found product:', foundProduct); // Debug log
        console.log('Product images:', foundProduct.images); // Debug log

        setProduct(foundProduct);

        // Fetch recommendations based on product category
        if (foundProduct.category) {
          fetchRecommendedProducts(foundProduct.category, foundProduct.id || foundProduct._id);
        }

      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch product');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
      setSliderIndex(0); // Reset slider when product changes
    }
  }, [productId, fetchRecommendedProducts]);

  useEffect(() => {
    const savedFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setFavorite(savedFavorites.includes(productId));
  }, [productId]);

  const toggleFavorite = () => {
    const newFavorite = !favorite;
    setFavorite(newFavorite);

    const savedFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    const updatedFavorites = newFavorite
      ? [...savedFavorites, productId]
      : savedFavorites.filter(id => id !== productId);

    localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
  };

  const handleImageChange = (index) => {
    setCurrentImageIndex(index);
  };

  const navigateImages = (direction) => {
    if (!product?.images) return;

    const newIndex = direction === 'next'
      ? (currentImageIndex + 1) % product.images.length
      : (currentImageIndex - 1 + product.images.length) % product.images.length;

    setCurrentImageIndex(newIndex);
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity > 0 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price).replace('₹', '₹ ');
  };

  // Slider navigation
  const nextSlide = () => {
    const maxIndex = Math.max(0, recommendedProducts.length - 4);
    setSliderIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setSliderIndex(prev => Math.max(prev - 1, 0));
  };

  const getProductId = (product) => {
    return product.id || product._id;
  };

  const handleRecommendedProductClick = (recommendedProduct) => {
    const recProductId = getProductId(recommendedProduct);
    navigate(`/products/${recProductId}`);
  };
  // useEffect(() => {
  //   if (addToCartSuccess) {
  //     toast.success(
  //       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  //         Product added to cart  (Click to view cart)
  //       </div>,
  //       {
  //         onClick: () => navigate('/cart'), // makes toast clickable
  //         autoClose: 3000, // automatically closes after 3 seconds
  //       }
  //     );
  //   }
  // }, [addToCartSuccess, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>Error loading product</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Try Again
        </button>
        {/* <button className="back-button" onClick={() => navigate(-1)}>
          Go Back
        </button> */}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="not-found-container">
        <h2>Product not found</h2>
        <p>The product you're looking for doesn't exist or may have been removed.</p>
        <button className="back-button" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }
  const fullDescription = product?.description || 'No description available.';
  const isDescriptionLong = fullDescription.length > DESCRIPTION_MAX_LENGTH;
  const displayDescription = showFullDescription || !isDescriptionLong
    ? fullDescription
    : fullDescription.substring(0, DESCRIPTION_MAX_LENGTH) + '...';
  return (
    <div className="product-detail-container">
      {/* <div className="breadcrumb-nav">
        <button className="back-button" onClick={() => navigate(-1)} aria-label="Go back">
          <ChevronLeft size={18} />
          Back to Products
        </button>
      </div> */}

      <div className="product-detail-content">
        {/* Product Images Section */}
        <div className="product-images-section">
          <div className="main-image-container" onMouseMove={handleZoomMove} onMouseEnter={() => setZoom(true)} onMouseLeave={() => setZoom(false)}>
            {product.images && product.images.length > 0 ? (
              <>
                <img
                  ref={mainImageRef}
                  src={getImageUrl(product.images[currentImageIndex].path)}
                  alt={product.name}
                  className="main-product-image"
                  loading="eager"
                  onError={(e) => {
                    console.error("Main image failed to load:", e.target.src);
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/400x400?text=No+Image";
                  }}
                  onLoad={(e) => {
                    console.log("Main image loaded successfully:", e.target.src);
                  }}
                />
                {product.images.length > 1 && (
                  <>
                    <button
                      className="image-nav-button prev"
                      onClick={() => navigateImages('prev')}
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      className="image-nav-button next"
                      onClick={() => navigateImages('next')}
                      aria-label="Next image"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="image-placeholder">
                <span>📦</span>
                <p>No image available</p>
              </div>
            )}
          </div>

          {product.images && product.images.length > 1 && (
            <div className="thumbnail-container">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  className={`thumbnail-button ${currentImageIndex === index ? 'active' : ''}`}
                  onClick={() => handleImageChange(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={getImageUrl(image.path)}
                    alt={`${product.name} thumbnail ${index}`}
                    className="thumbnail-image"
                    onError={(e) => {
                      console.error("Thumbnail failed to load:", e.target.src);
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/100x100?text=No+Image";
                    }}
                  />
                </button>
              ))}
            </div>
          )}

        </div>
        <div className='zoom-preview' style={{ display: zoom ? "block" : "none" }}>
          <div className='zoom-preview-image' style={{ background: `url(${getImageUrl(product.images[currentImageIndex].path)})`, backgroundPosition: zoomPos }}>
          </div>
        </div>
        {/* Product Info Section */}
        <div className="product-info-section">
          <div className="product-header">
            <div>
              <span className="product-category">{product.category || 'Product'}</span>
              <h1 className="product-title">{product.name}</h1>
            </div>
            {/* <button
              className={`favorite-button ${favorite ? 'active' : ''}`}
              onClick={toggleFavorite}
              aria-label={favorite ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart size={24} />
            </button> */}
          </div>

          <div className="product-meta">
            <div className="product-rating">
              <Star size={18} fill="#FFD700" color="#FFD700" />
              <span>{product.rating || "4.0"}</span>
              <span className="reviews">(42 reviews)</span>
            </div>
            <div
              className={`availability-badge ${product.stock > 0 || product.inStock ? 'in-stock' : 'out-of-stock'
                }`}
            >
              {product.stock > 0 || product.inStock ? (
                <>
                  <Check size={14} />
                  In Stock
                </>
              ) : (
                <>
                  <Info size={14} />
                  Out of Stock
                </>
              )}
            </div>

          </div>

          <div className="product-price-container">
            <span className="current-price">{formatPrice(product.price)}</span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="original-price">
                {formatPrice(product.originalPrice)}
              </span>
            )}
            {product.discount && product.discount > 0 && (
              <span className="discount-badge">
                {product.discount}% OFF
              </span>
            )}
          </div>

          <div className="product-tabs">
            <button
              className={`tab-button ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button
              className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('specifications')}
            >
              Specifications
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="product-description">
                {/* Use the calculated displayDescription state */}
                <p>
                  {displayDescription}
                  {/* Conditionally render the button */}
                  {isDescriptionLong && (
                    <button
                      className="read-more-button"
                      onClick={() => setShowFullDescription(prev => !prev)}
                    >
                      {showFullDescription ? 'Show Less' : 'Read More'}
                    </button>
                  )}
                </p>
              </div>
            )}

            {activeTab === 'specifications' && product.specifications && (
              <div className="product-specifications">
                <div className="specs-grid">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="spec-item">
                      <div className="spec-key">{key}</div>
                      <div className="spec-value">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="product-actions">
            <div className="quantity-selector">
              <button
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span>{quantity}</span>
              <button
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 10}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <button
              className={`add-to-cart-button ${addToCartSuccess ? 'success' : ''}`}
              onClick={handleAddToCart}
              disabled={addingToCart}
            >
              {addingToCart ? (
                <>
                  <div className="button-spinner"></div>
                  Adding...
                </>
              ) : addToCartSuccess ? (
                <>
                  <Check size={18} />
                  Added to Cart!
                </>
              ) : (
                <>
                  <ShoppingCart size={18} />
                  Add to Cart
                </>
              )}
            </button>
          </div>

          {/* Success Message */}
          {/* {addToCartSuccess && (
            <div className="cart-success-message">
              <Check size={16} />
              <span>Product added to cart successfully!</span>
              <button onClick={() => navigate('/cart')} className="view-cart-link">
                View Cart
              </button>
            </div>
          )} */}

          {/* Trust badges */}
          <div className="trust-badges">
            <div className="trust-badge">
              <Truck size={16} />
              <span>Free Delivery</span>
            </div>
            <div className="trust-badge">
              <Shield size={16} />
              <span>Secure Payment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations Section */}
      {product.category && (
        <div className="recommendations-section">
          <div className="recommendations-header">
            <h2>Similar Products in {product.category}</h2>
            <p>You might also like these products</p>
          </div>

          {recommendationsLoading ? (
            <div className="recommendations-loading">
              <div className="loading-spinner"></div>
              <p>Loading recommendations...</p>
            </div>
          ) : recommendedProducts.length > 0 ? (
            <div className="recommendations-slider-container">
              <div className="slider-controls">
                <button
                  className="slider-button prev"
                  onClick={prevSlide}
                  disabled={sliderIndex === 0}
                  aria-label="Previous products"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="slider-button next"
                  onClick={nextSlide}
                  disabled={sliderIndex >= Math.max(0, recommendedProducts.length - 4)}
                  aria-label="Next products"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="recommendations-slider">
                <div
                  className="recommendations-track"
                  style={{
                    transform: `translateX(-${sliderIndex * 25}%)`,
                    transition: 'transform 0.3s ease'
                  }}
                >
                  {recommendedProducts.map((recProduct, index) => (
                    <div key={getProductId(recProduct)} className="recommendation-card">
                      <div className="recommendation-image-container">
                        <img
                          src={
                            recProduct.images && recProduct.images.length > 0
                              ? getImageUrl(recProduct.images[0].path)
                              : "https://via.placeholder.com/200x200?text=No+Image"
                          }
                          alt={recProduct.name}
                          className="recommendation-image"
                          onError={(e) => {
                            console.error("Recommendation image failed to load:", e.target.src);
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/200x200?text=No+Image";
                          }}
                        />
                        <div className="recommendation-overlay">
                          <button
                            className="view-product-button"
                            onClick={() => handleRecommendedProductClick(recProduct)}
                          >
                            View Product
                          </button>
                        </div>
                      </div>

                      <div className="recommendation-info">
                        <h4 className="recommendation-title">{recProduct.name.length > 10 ? recProduct.name.slice(0, 10) + "..." : recProduct.name}</h4>
                        <div className="recommendation-rating">
                          <Star size={14} fill="#FFD700" color="#FFD700" />
                          <span>{recProduct.rating || "4.0"}</span>
                        </div>
                        <div className="recommendation-price">
                          <span className="rec-current-price">
                            {formatPrice(recProduct.price)}
                          </span>
                          {recProduct.originalPrice && recProduct.originalPrice > recProduct.price && (
                            <span className="rec-original-price">
                              {formatPrice(recProduct.originalPrice)}
                            </span>
                          )}
                        </div>
                        <button
                          className="quick-add-button"
                          onClick={() => handleRecommendedProductClick(recProduct)}
                        >
                          <ShoppingCart size={16} />
                          Quick View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-recommendations">
              <p>No similar products found in this category.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;